import type { ParseOptions } from '@noths/polaris-product-listings-urls';
import { parse, RouteParseError } from '@noths/polaris-product-listings-urls';

/**
 * The URL's filter values, lower-cased, in a deterministic order (by alias, then
 * value). These are the selections after the `/-/` separator — the taxonomy
 * levels are excluded.
 *
 * Used by `selectPageCategories` to append the filter breadcrumb after the
 * product-type levels ({@link levelsToPageCategories}) on an exact, indexable
 * content match.
 *
 * Returns an empty array when the path carries no filters or cannot be parsed
 * against the given route (a non-shop path is simply "no filters" here).
 */
export const getUrlFilterValues = (pathname: string, options: ParseOptions): string[] => {
  let selections;

  try {
    selections = parse(pathname, options);
  } catch (error) {
    if (!(error instanceof RouteParseError)) {
      throw error;
    }

    return [];
  }

  // Locale-invariant ordering (UTF-16 code units), lower-cased before sorting so
  // the emitted breadcrumb is alphabetical as displayed regardless of casing.
  const byCodeUnit = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

  return selections
    .filter((selection) => selection.part === 'filter')
    .sort((a, b) => byCodeUnit(a.alias, b.alias))
    .flatMap((selection) => selection.values.map((value) => value.toLowerCase()).sort(byCodeUnit));
};
